import { PaginationControls } from "@/components/pagination-controls";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useServerPagination } from "@/hooks/use-pagination";
import { categoryService } from "@/lib/api/categories.service";
import {
  ContentOperationService,
  type DashboardPage as DashboardItemsPage,
  type DashboardRow,
} from "@/lib/api/content.operation.service";
import { useAuth } from "@/lib/auth";
import { withFromParam } from "@/lib/back-nav";
import { CONTENT_TYPES } from "@/lib/content-types";
import type { Category } from "@/lib/schema";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ExternalLink,
  FileText,
  Filter,
  Globe,
  Tags,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

function ContentTypeIcon({ type }: { type: string }) {
  const Icon = CONTENT_TYPES[type as keyof typeof CONTENT_TYPES]?.icon ?? FileText;
  return <Icon className="h-4 w-4 text-muted-foreground" />;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  es: "Spanish",
  pt: "Portuguese",
  fr: "French",
};

export default function LivePage() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const canEditItem = (createdBy: string) => {
    if (!user) return false;
    if (user.roles.includes("coach")) return createdBy === user.id;

    return (
      user.roles?.includes("admin") ||
      user.roles?.includes("super_admin") ||
      user.roles?.includes("coach_manager")
    );
  };
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [languageFilter, setLanguageFilter] = useState<string>("all");
  const [coachFilter, setCoachFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [typeFilter, languageFilter, coachFilter, categoryFilter]);

  const { data, isLoading } = useQuery<DashboardItemsPage>({
    queryKey: [
      "content-published-items",
      { typeFilter, coachFilter, categoryFilter, languageFilter, page },
    ],
    queryFn: () =>
      ContentOperationService.getDashboardItems({
        status: "published",
        type: typeFilter,
        ownerId: coachFilter,
        categoryId: categoryFilter,
        languageCode: languageFilter,
        page,
        limit: 10,
      }),
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  // Owners/coaches list comes from the summary endpoint (all content, not just
  // published) — good enough for the filter dropdown without a full payload fetch.
  const { data: countsData } = useQuery({
    queryKey: ["/content/dashboard/summary"],
    queryFn: () => ContentOperationService.getDashboardCounts(),
  });

  const availableLanguages = Object.keys(LANGUAGE_LABELS);
  const availableCoaches = countsData?.owners ?? [];

  // language is filtered server-side via the languageCode param
  const filteredItems = data?.items ?? [];

  const paginationProps = useServerPagination({
    page: data?.page ?? page,
    total: data?.total ?? 0,
    pageSize: data?.limit ?? 10,
    onPageChange: setPage,
  });

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="live-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/content-operations")}
          data-testid="button-back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="text-page-title"
          >
            Live Content Operations
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage all currently live content across the platform
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[140px]" data-testid="select-type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.values(CONTENT_TYPES).map((meta) => (
              <SelectItem key={meta.key} value={meta.key}>
                {meta.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={languageFilter} onValueChange={setLanguageFilter}>
          <SelectTrigger
            className="w-[140px]"
            data-testid="select-language-filter"
          >
            <SelectValue placeholder="Language" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Languages</SelectItem>
            {availableLanguages.map((code) => (
              <SelectItem key={code} value={code}>
                {LANGUAGE_LABELS[code] || code.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={coachFilter} onValueChange={setCoachFilter}>
          <SelectTrigger
            className="w-[160px]"
            data-testid="select-coach-filter"
          >
            <SelectValue placeholder="Coach" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Coaches</SelectItem>
            {availableCoaches.map((coach) => (
              <SelectItem key={coach.id} value={coach.id}>
                {coach.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger
            className="w-[160px]"
            data-testid="select-category-filter"
          >
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data && (
          <span
            className="text-sm text-muted-foreground ml-auto tabular-nums"
            data-testid="text-item-count"
          >
            {filteredItems.length} items
          </span>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : !data ? null : (
        <div className="border rounded-md overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Type</TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead className="hidden lg:table-cell">
                  Languages
                </TableHead>
                <TableHead className="hidden lg:table-cell">Tags</TableHead>
                <TableHead className="hidden md:table-cell">Author</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground py-8"
                  >
                    No live content found matching your filters
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item: DashboardRow) => (
                  <TableRow
                    key={`${item.contentType}-${item.id}`}
                    data-testid={`row-live-${item.id}`}
                  >
                    <TableCell>
                      <ContentTypeIcon type={item.contentType} />
                    </TableCell>
                    <TableCell
                      className="font-medium max-w-[200px] truncate"
                      data-testid={`text-title-${item.id}`}
                    >
                      {item.title}
                    </TableCell>
                    <TableCell
                      className="hidden md:table-cell text-sm text-muted-foreground"
                      data-testid={`text-category-${item.id}`}
                    >
                      {item.categoryName || "—"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex items-center gap-1">
                        <Globe className="h-3 w-3 text-muted-foreground" />
                        <span
                          className="text-sm tabular-nums"
                          data-testid={`text-languages-${item.id}`}
                        >
                          {item.languageCount}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex items-center gap-1">
                        <Tags className="h-3 w-3 text-muted-foreground" />
                        <span
                          className="text-sm tabular-nums"
                          data-testid={`text-tags-${item.id}`}
                        >
                          {item.tagCount}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell
                      className="hidden md:table-cell text-sm text-muted-foreground"
                      data-testid={`text-author-${item.id}`}
                    >
                      {item.createdByName || "System"}
                    </TableCell>
                    <TableCell
                      className="text-sm text-muted-foreground whitespace-nowrap"
                      data-testid={`text-updated-${item.id}`}
                    >
                      {formatRelativeTime(item.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1.5"
                        onClick={() =>
                          setLocation(withFromParam(item.editUrl, location))
                        }
                        data-testid={
                          canEditItem(item.createdBy)
                            ? `button-open-${item.id}`
                            : `button-view-${item.id}`
                        }
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {canEditItem(item.createdBy) ? "Open" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <PaginationControls
            currentPage={paginationProps.currentPage}
            totalPages={paginationProps.totalPages}
            totalItems={paginationProps.totalItems}
            startIndex={paginationProps.startIndex}
            endIndex={paginationProps.endIndex}
            canGoNext={paginationProps.canGoNext}
            canGoPrev={paginationProps.canGoPrev}
            onPageChange={paginationProps.setPage}
            onNext={paginationProps.nextPage}
            onPrev={paginationProps.prevPage}
          />
        </div>
      )}
    </div>
  );
}
